import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LifeBuoy, Loader2, Search, Coins, LogOut, Shield, Star, Lightbulb,
  Ticket, AlertOctagon, RefreshCw,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { AdminTicketDialog } from "../components/support/AdminTicketDialog";
import {
  STATUS_STYLE, SEVERITY_STYLE, STATUS_LABEL, fmtDate, fmtDay,
} from "../lib/support";

const CATEGORIES = [
  ["subtitle_translation", "Subtitle / Translation"],
  ["transcription", "Transcription"],
  ["subtitle_timing", "Subtitle Timing"],
  ["video_rendering", "Video / Rendering"],
  ["processing_failed", "Processing Failed or Stuck"],
  ["download_problem", "Download Problem"],
  ["payment_credits", "Payment / Credits"],
  ["account_login", "Account / Login"],
  ["other", "Other"],
];
const SEVERITIES = ["minor", "incorrect", "major", "critical"];
const STATUSES = ["new", "investigating", "waiting_for_user", "resolved", "reopened"];

const Counter = ({ label, value, tone = "zinc", icon: Icon, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left border p-5 transition-colors duration-150 ${
      active ? "border-white bg-zinc-900" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-600"
    }`}
  >
    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
      {Icon ? <Icon className="w-3 h-3" /> : null} {label}
    </div>
    <div className={`font-heading text-3xl font-black tracking-tighter ${
      tone === "red" ? "text-red-400" : tone === "emerald" ? "text-emerald-400" : "text-white"
    }`}>
      {value ?? "—"}
    </div>
  </button>
);

export default function AdminSupportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("tickets");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [filters, setFilters] = useState({
    status: "all", category: "all", severity: "all", date_from: "", date_to: "",
  });
  const [q, setQ] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [features, setFeatures] = useState(null);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all") params.set(k, v);
      });
      if (q.trim()) params.set("q", q.trim());
      const [s, list] = await Promise.all([
        api.get("/admin/support/stats"),
        api.get(`/admin/support/tickets?${params.toString()}`),
      ]);
      setStats(s.data);
      setRows(list.data.tickets || []);
      setTotal(list.data.total || 0);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Admins only");
        navigate("/dashboard");
      } else {
        toast.error("Could not load support tickets");
      }
    } finally {
      setLoading(false);
    }
  }, [filters, q, navigate]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  useEffect(() => {
    if (tab === "feedback" && !feedback) {
      api.get("/admin/support/feedback").then(({ data }) => setFeedback(data)).catch(() => {});
    }
    if (tab === "features" && !features) {
      api.get("/admin/support/feature-requests").then(({ data }) => setFeatures(data)).catch(() => {});
    }
  }, [tab, feedback, features]);

  const setFeatureStatus = async (id, status) => {
    try {
      await api.post(`/admin/support/feature-requests/${id}/status`, { status });
      const { data } = await api.get("/admin/support/feature-requests");
      setFeatures(data);
      toast.success(`${id} → ${status}`);
    } catch {
      toast.error("Could not update");
    }
  };

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <LifeBuoy className="w-4 h-4 text-blue-400 shrink-0" />
            <div className="font-heading text-lg font-bold tracking-tight truncate">
              Support Centre
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span className="font-mono hidden md:inline">{user?.email}</span>
            <Link
              to="/admin"
              data-testid="support-admin-nav-link"
              className="border border-zinc-800 hover:bg-zinc-800 px-3 py-1.5 text-xs uppercase font-mono tracking-widest"
            >
              <Shield className="w-3 h-3 inline mr-1" /> Admin
            </Link>
            <Link
              to="/admin/spend"
              className="border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 px-3 py-1.5 text-xs uppercase font-mono tracking-widest hidden sm:inline-block"
            >
              <Coins className="w-3 h-3 inline mr-1" /> Spend
            </Link>
            <Link
              to="/dashboard"
              className="border border-zinc-800 hover:bg-zinc-800 px-3 py-1.5 text-xs uppercase font-mono tracking-widest"
            >
              <LogOut className="w-3 h-3 inline mr-1" /> Exit
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-10 space-y-8" data-testid="admin-support-page">
        <div className="flex flex-wrap gap-2">
          {[
            ["tickets", "Tickets", Ticket],
            ["feedback", "Feedback", Star],
            ["features", "Feature requests", Lightbulb],
          ].map(([key, label, Icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`admin-support-tab-${key}`}
              className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest px-4 py-2.5 border transition-colors duration-150 ${
                tab === key
                  ? "border-white bg-white text-zinc-950"
                  : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
              }`}
            >
              <Icon className="w-3 h-3" /> {label}
            </button>
          ))}
        </div>

        {tab === "tickets" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="support-stats">
              {STATUSES.map((s) => (
                <Counter
                  key={s}
                  label={STATUS_LABEL[s]}
                  value={stats?.by_status?.[s]}
                  tone={s === "resolved" ? "emerald" : s === "reopened" ? "red" : "zinc"}
                  active={filters.status === s}
                  onClick={() => setFilters((f) => ({ ...f, status: f.status === s ? "all" : s }))}
                  testId={`support-stat-${s}`}
                />
              ))}
              <Counter
                label="Critical open"
                value={stats?.critical_open}
                tone="red"
                icon={AlertOctagon}
                active={filters.severity === "critical"}
                onClick={() => setFilters((f) => ({
                  ...f, severity: f.severity === "critical" ? "all" : "critical",
                }))}
                testId="support-stat-critical"
              />
            </div>

            <div className="border border-zinc-800 bg-zinc-900/40 p-5 grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Search</div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    data-testid="support-search-input"
                    placeholder="Ticket ID, customer email, job ID"
                    className="bg-zinc-950 border-zinc-800 rounded-none h-10 pl-9 text-white placeholder:text-zinc-600"
                  />
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Status</div>
                <Select
                  value={filters.status}
                  onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger data-testid="support-filter-status" className="bg-zinc-950 border-zinc-800 rounded-none h-10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Category</div>
                <Select
                  value={filters.category}
                  onValueChange={(v) => setFilters((f) => ({ ...f, category: v }))}
                >
                  <SelectTrigger data-testid="support-filter-category" className="bg-zinc-950 border-zinc-800 rounded-none h-10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                    <SelectItem value="all">All categories</SelectItem>
                    {CATEGORIES.map(([k, l]) => (
                      <SelectItem key={k} value={k}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Severity</div>
                <Select
                  value={filters.severity}
                  onValueChange={(v) => setFilters((f) => ({ ...f, severity: v }))}
                >
                  <SelectTrigger data-testid="support-filter-severity" className="bg-zinc-950 border-zinc-800 rounded-none h-10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                    <SelectItem value="all">All severities</SelectItem>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">From</div>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                    data-testid="support-filter-date-from"
                    className="w-full bg-zinc-950 border border-zinc-800 h-10 px-2 text-xs text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">To</div>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                    data-testid="support-filter-date-to"
                    className="w-full bg-zinc-950 border border-zinc-800 h-10 px-2 text-xs text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="border border-zinc-800 bg-zinc-900/40" data-testid="support-ticket-table">
              <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {total} ticket{total === 1 ? "" : "s"}
                </div>
                <button
                  type="button"
                  onClick={loadTickets}
                  data-testid="support-refresh-btn"
                  className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>

              {loading ? (
                <div className="py-16 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
                </div>
              ) : rows.length === 0 ? (
                <div className="py-16 text-center text-sm text-zinc-500" data-testid="support-no-tickets">
                  No tickets match these filters.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                        <th className="text-left px-5 py-3">Ticket</th>
                        <th className="text-left px-3 py-3">Date</th>
                        <th className="text-left px-3 py-3">Customer</th>
                        <th className="text-left px-3 py-3">Category</th>
                        <th className="text-left px-3 py-3">Job</th>
                        <th className="text-left px-3 py-3">Severity</th>
                        <th className="text-left px-3 py-3">Status</th>
                        <th className="text-left px-3 py-3">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {rows.map((t) => (
                        <tr
                          key={t.ticket_id}
                          onClick={() => setOpenId(t.ticket_id)}
                          data-testid={`support-row-${t.ticket_id}`}
                          className="hover:bg-zinc-900/60 cursor-pointer transition-colors duration-150"
                        >
                          <td className="px-5 py-3 font-mono text-xs text-white whitespace-nowrap">{t.ticket_id}</td>
                          <td className="px-3 py-3 font-mono text-[11px] text-zinc-500 whitespace-nowrap">{fmtDay(t.created_at)}</td>
                          <td className="px-3 py-3 text-xs text-zinc-300 max-w-[180px] truncate">{t.customer_email}</td>
                          <td className="px-3 py-3 text-xs text-zinc-300 whitespace-nowrap">{t.category_label}</td>
                          <td className="px-3 py-3 font-mono text-[11px] text-zinc-500 max-w-[140px] truncate">
                            {t.job_id || "—"}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${SEVERITY_STYLE[t.severity] || ""}`}>
                              {t.severity}
                            </span>
                            {t.severity !== t.reported_severity && (
                              <span className="ml-1.5 text-[10px] font-mono text-zinc-600">
                                (rep. {t.reported_severity})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${STATUS_STYLE[t.status] || ""}`}>
                              {t.status_label}
                            </span>
                          </td>
                          <td className="px-3 py-3 font-mono text-[11px] text-zinc-500 whitespace-nowrap">{fmtDate(t.updated_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {tab === "feedback" && (
          <div className="border border-zinc-800 bg-zinc-900/40" data-testid="admin-feedback-panel">
            <div className="px-5 py-4 border-b border-zinc-800 flex flex-wrap items-center gap-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Customer feedback
              </div>
              {feedback?.average_rating != null && (
                <div className="flex items-center gap-1.5 text-xs font-mono text-amber-300" data-testid="feedback-average">
                  <Star className="w-3 h-3 fill-amber-300" /> {feedback.average_rating}/5 avg
                </div>
              )}
              <div className="text-[10px] font-mono text-zinc-600 ml-auto">
                {feedback?.count ?? 0} entries
              </div>
            </div>
            {!feedback ? (
              <div className="py-16 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
            ) : feedback.items.length === 0 ? (
              <div className="py-16 text-center text-sm text-zinc-500">No feedback yet.</div>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {feedback.items.map((f) => (
                  <li key={f.feedback_id} className="px-5 py-4" data-testid={`feedback-item-${f.feedback_id}`}>
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`w-3 h-3 ${n <= f.rating ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`}
                          />
                        ))}
                      </span>
                      {f.category && (
                        <span className="text-[10px] font-mono uppercase tracking-widest border border-zinc-700 text-zinc-400 px-1.5 py-0.5">
                          {f.category}
                        </span>
                      )}
                      <span className="text-xs text-zinc-500">{f.user_email}</span>
                      <span className="text-[10px] font-mono text-zinc-600 ml-auto">{fmtDate(f.created_at)}</span>
                    </div>
                    <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{f.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "features" && (
          <div className="border border-zinc-800 bg-zinc-900/40" data-testid="admin-features-panel">
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                Feature requests
              </div>
              <div className="text-[10px] font-mono text-zinc-600 ml-auto">
                {features?.count ?? 0} submitted
              </div>
            </div>
            {!features ? (
              <div className="py-16 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
            ) : features.items.length === 0 ? (
              <div className="py-16 text-center text-sm text-zinc-500">No requests yet.</div>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {features.items.map((r) => (
                  <li key={r.request_id} className="px-5 py-5 space-y-2" data-testid={`feature-item-${r.request_id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-mono text-[11px] text-zinc-500">{r.request_id}</span>
                      <span className="text-white font-semibold tracking-tight">{r.title}</span>
                      <span className="text-xs text-zinc-500">{r.user_email}</span>
                      <div className="ml-auto flex items-center gap-2">
                        <Select value={r.status} onValueChange={(v) => setFeatureStatus(r.request_id, v)}>
                          <SelectTrigger
                            data-testid={`feature-status-${r.request_id}`}
                            className="bg-zinc-950 border-zinc-800 rounded-none h-8 w-40 text-white text-xs"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
                            {(features.statuses || []).map((s) => (
                              <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-[10px] font-mono text-zinc-600">{fmtDay(r.created_at)}</span>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{r.description}</p>
                    {r.why_useful && (
                      <p className="text-xs text-zinc-500 leading-relaxed border-l-2 border-zinc-800 pl-3">
                        Why: {r.why_useful}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </main>

      {openId && (
        <AdminTicketDialog
          ticketId={openId}
          open={!!openId}
          onOpenChange={(v) => { if (!v) setOpenId(null); }}
          onChanged={loadTickets}
        />
      )}
    </div>
  );
}
